import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import FiberPopListMap from './fiber_pop_list_map.component';
import FiberPopListTable from './fiber_pop_list_table.component';
import FiberPopFilter from './fiber_pop_filter.component';
import { FiberPopListHeader } from './fiber_pop_list_header.component';
import AddSinglePopFiberPopListComponent from './add_single_pop_fiber_pop_list.component';
import FiberPopHeaderButtons from './fiber_pop_header_buttons.component';
import FiberPopUploadModal from './FiberPopUploadModal';
import FiberPopUploadReviewModal from './FiberPopUploadReviewModal';
import FiberPopBulkDeleteModal from './FiberPopBulkDeleteModal';
import FiberPopBulkEditModal from './FiberPopBulkEditModal';
import apiClient from '../../common/functions/api_axios_instance.function';
import { GET_ALL_FIBER_POP } from '../../common/constants/constants';
import { handleApiError } from '../../common/functions/errorHandler.function';

const FiberPopListMain = () => {
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'table'
  const [selectedFiberPop, setSelectedFiberPop] = useState('');
  const [mapRef, setMapRef] = useState(null);
  const [fiberPopData, setFiberPopData] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [showAddSinglePopForm, setShowAddSinglePopForm] = useState(false);
  const [popToEdit, setPopToEdit] = useState(null);
  const [showBulkUploadForm, setShowBulkUploadForm] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [reviewPreviewData, setReviewPreviewData] = useState(null);
  const controllerRef = useRef(null);

  // Helper function to parse coordinate value
  const parseCoordinateValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const cleaned = value.trim().replace(/[°\s]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Transform API response to component-expected format
  const transformApiData = (apiResponse) => {
    const fiberPops = apiResponse?.response || [];
    const transformedData = [];

    fiberPops.forEach((pop) => {
      // Parse coordinates - API uses 'latitude' and 'longitude'
      const lat = parseCoordinateValue(pop.latitude);
      const lng = parseCoordinateValue(pop.longitude);

      // Include all records, even if coordinates are invalid
      // Set coordinates to null if invalid (will be handled in display/map)
      // Map API fields to component-expected format
      transformedData.push({
        fiber_pop: pop.fiber_pop || '--',
        latest_status: pop.latest_status || '--',
        ran_region: pop.ran_region || '--',
        foo_region: pop.foo_region || '--',
        city_name: pop.city_name || '--',
        type: pop.type || '--',
        telecom_region: pop.telecom_region || '--',
        ran_site: pop.ran_site_flag || '--',
        ola_location: pop.ola_location_flag || '--',
        site_name: pop.site_name || '--',
        latt: lat !== null ? lat : null, // Keep null for invalid coordinates
        long: lng !== null ? lng : null, // Keep null for invalid coordinates
        technology: pop.technology || '--',
        vendor: pop.vendor || '--',
        route: pop.route || '--',
        id: pop.id,
      });
    });

    return transformedData;
  };

  // Fetch fiber POP data from API (only once, shared between views)
  // silent: if true, don't show error notifications (for background refreshes)
  const fetchFiberPopData = async (silent = false) => {
    let requestId = null;
    try {
      // Cancel any existing requests
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      controllerRef.current = new AbortController();
      requestId = `fiber-pop-data-${Date.now()}`;

      const response = await apiClient.get(GET_ALL_FIBER_POP, {
        signal: controllerRef.current.signal,
      });

      // API returns { status, message, response: [...] }
      const apiData = response?.data || {};

      // Transform the API response to match component expectations
      const transformedData = transformApiData(apiData);
      console.log('Transformed fiber POP data:', transformedData);
      setFiberPopData(transformedData);
    } catch (error) {
      // Check for cancelled/aborted requests - don't show error notifications for these
      if (
        error.name === 'AbortError' ||
        error.code === 'ERR_CANCELED' ||
        error.message === 'canceled' ||
        error.message === 'Cancelled'
      ) {
        console.log('[FiberPopMain] Request aborted/cancelled');
        return;
      }

      console.error('Error fetching fiber POP data:', error);
      // Only show error notifications if not in silent mode
      if (!silent) {
        handleApiError(error);
      }
      // Don't clear data on error in silent mode (keep existing data)
      if (!silent) {
        setFiberPopData([]);
      }
    }
  };

  // Fetch data on component mount (only once)
  useEffect(() => {
    console.log('Fetching fiber POP data in parent component...');
    fetchFiberPopData();

    // Cleanup: cancel request only when entire component unmounts
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  const handleAddSinglePop = () => {
    setPopToEdit(null);
    setShowAddSinglePopForm(true);
  };

  const handleEditPop = (row) => {
    setPopToEdit(row || null);
    setShowAddSinglePopForm(true);
  };

  const handleBulkUpload = () => {
    setShowBulkUploadForm(true);
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const handleBulkEdit = () => {
    setShowBulkEditModal(true);
  };

  const handleBulkEditSuccess = (updatedRows) => {
    if (!updatedRows?.length) {
      return;
    }
    setFiberPopData((prevData) =>
      prevData.map((pop) => {
        const updated = updatedRows.find((r) => r.id === pop.id);
        return updated ? { ...pop, ...updated } : pop;
      })
    );
  };

  const handlePopAdded = (updatedRow) => {
    setShowAddSinglePopForm(false);
    setPopToEdit(null);
    if (updatedRow?.id) {
      setFiberPopData((prevData) =>
        prevData.map((pop) => (pop.id === updatedRow.id ? updatedRow : pop))
      );
    } else {
      fetchFiberPopData(true);
    }
  };

  const handleCancelAddPop = () => {
    setShowAddSinglePopForm(false);
    setPopToEdit(null);
  };

  const handleDeleteSuccess = (deletedId) => {
    // Update local state by filtering out the deleted item(s)
    // Can accept either a single ID or an array of IDs
    const idsToDelete = Array.isArray(deletedId) ? deletedId : [deletedId];
    setFiberPopData((prevData) => prevData.filter((pop) => !idsToDelete.includes(pop.id)));
  };

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, p: 0, position: 'relative' }}>
        {showAddSinglePopForm ? (
          <AddSinglePopFiberPopListComponent
            onPopAdded={handlePopAdded}
            onCancel={handleCancelAddPop}
            popToEdit={popToEdit}
          />
        ) : (
          <>
            {viewMode !== 'map' ? (
              <FiberPopListHeader viewMode={viewMode} setViewMode={setViewMode} />
            ) : null}

            <FiberPopHeaderButtons
              viewType={viewMode}
              setViewType={setViewMode}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />

            {viewMode === 'map' && (
              <FiberPopFilter
                selectedFiberPop={selectedFiberPop}
                setSelectedFiberPop={setSelectedFiberPop}
                mapRef={mapRef}
                fiberPopData={fiberPopData}
                viewType={viewMode}
                showFilter={showFilter}
              />
            )}

            <Box
              sx={{
                marginTop: viewMode === 'table' ? '0px' : '0px',
                padding: viewMode === 'table' ? '0px' : '0px',
              }}
            >
              {viewMode === 'map' ? (
                <FiberPopListMap
                  selectedFiberPop={selectedFiberPop}
                  setMapRef={setMapRef}
                  fiberPopData={fiberPopData}
                />
              ) : (
                <FiberPopListTable
                  fiberPopData={fiberPopData}
                  selectedFiberPop={selectedFiberPop}
                  onAddSinglePop={handleAddSinglePop}
                  onBulkUpload={handleBulkUpload}
                  onBulkDelete={handleBulkDelete}
                  onBulkEdit={handleBulkEdit}
                  onDeleteSuccess={handleDeleteSuccess}
                  onEditPop={handleEditPop}
                />
              )}
            </Box>
          </>
        )}
      </Box>
      {/* Upload flow modals for Fiber POP */}
      <FiberPopUploadModal
        open={showBulkUploadForm}
        onClose={() => setShowBulkUploadForm(false)}
        onUploadComplete={(preview) => setReviewPreviewData(preview)}
      />
      <FiberPopUploadReviewModal
        open={Boolean(reviewPreviewData)}
        onClose={() => setReviewPreviewData(null)}
        previewData={reviewPreviewData}
        onSynced={() => {
          setReviewPreviewData(null);
          fetchFiberPopData(true);
        }}
      />
      <FiberPopBulkDeleteModal
        open={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        fiberPopData={fiberPopData}
        onDeleteSuccess={handleDeleteSuccess}
      />
      <FiberPopBulkEditModal
        open={showBulkEditModal}
        onClose={() => setShowBulkEditModal(false)}
        fiberPopData={fiberPopData}
        onUpdateSuccess={handleBulkEditSuccess}
      />
    </DashboardLayout>
  );
};

export default FiberPopListMain;
